/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, June 2020
 */

import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { parseISO } from 'date-fns';
import { FlowBlockInput as BlockModel } from '@staffino/model';
import { useTranslation } from 'react-i18next';
import { ScrollAnchor, useScrollEffect } from './scroll';
import DateInputControl from './controls/date-input-control';

type Props = {
  block: BlockModel;
};

function parseDate(date: string | Date): Date {
  return typeof date === 'string' ? parseISO(date) : date;
}

function calculatePredefinedDate(block): Date {
  if (block.predefinedValue === 'Empty') {
    return null;
  }
  if (block.predefinedValue === 'ExactDate') {
    return parseDate(block.defaultDate);
  }
  if (Number.isInteger(block.predefinedValue)) {
    return new Date().getTime() + block.predefinedValue;
  }
  return new Date();
}

export default function DateInputBlock({ block }: Props) {
  const context = useFormContext();
  const defaultValue = calculatePredefinedDate(block);
  const invalid = !!context?.errors?.[block.id];

  const { t } = useTranslation();

  const [enterTriggered] = useState(false);
  const { scrollToError } = useScrollEffect(
    block.id,
    enterTriggered && !invalid ? 'validValue' : null,
  );
  const Component = context ? Controller : DateInputControl;

  return (
    <>
      <ScrollAnchor id={block.id} />
      <Component
        as={DateInputControl}
        name={block.id}
        rules={{ required: block.required }}
        label={block.label || t('Date')}
        labelShowMandatory={block.required}
        tooltip={{
          content: t('This field is required.'),
          show: block.required,
        }}
        placeholder={block.placeholder}
        width={1}
        maxWidth="15rem"
        m="1rem auto"
        defaultValue={defaultValue as any}
        invalid={invalid}
        requiredValidationMessage={
          context?.errors?.[block.id]?.type === 'required' &&
          t('Please fill out this field')
        }
        onFocus={() => scrollToError(block.id)}
        onChange={(e) => {
          return Array.isArray(e) ? e[0] : e.value;
        }}
      />
    </>
  );
}
